import { ReactComponent as IconEye } from "@/assets/svgs/Union-eye.svg"
import { ReactComponent as IconPerson } from '@/assets/svgs/icon-persona.svg'
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg'
import { ReactComponent as UserGroup } from "@/assets/svgs/user-group.svg"
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from '@/components/Button'
import CardUseCase from '@/components/CardUseCase'
import InputSuffixIcon from '@/components/InputSuffixIcon'
import Portal from '@/components/Portal'
import SelectorBox from '@/components/SelectorBox2'
import StepList from "@/components/StepList/index"
import Toast from '@/components/Toast'
import ToggleSwitch3 from '@/components/ToggleSwitch3/index'
import { EnrolmentRoles, EnrolmentSteps } from "@/constants/use-case/enrolment"
import useModal from '@/hooks/use-modal'
import usePage from '@/hooks/use-page'
import usePersona from '@/hooks/use-personas'
import useToast from '@/hooks/use-toast'
import useUseCases from '@/hooks/use-use-cases'
import { useLocation, useNavigate, useParams } from '@/lib/router-dom'
import api from '@/services/api/orgPersonas.js'
import { getUseCaseId } from "@/utils/use-case-storage"
import produce from 'immer'
import { orderBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import AgentPrincipalInformationModal from './components/AgentPrincipalInformationModal/index'
import AgentPrincipalTnCModal from './components/AgentPrincipalTnCModal/index'
import DataElementCard from "./components/ConfirmationList/DataElementCard"
import PersonaInfoModal from "./components/PersonaInfoModal/index"
import Stepper from "@/componentsDS/Stepper/Stepper"
import { Box } from "@dextech-ai/dex-design-system"


const Page = ({ role }) => {

	/*=================================================
		General
	/================================================*/

	const { organizationId, useCaseId } = useParams()
	const useCase = useMemo(() => getUseCaseId(useCaseId), [useCaseId]);

	const [pageTitle, setPageTitle] = useState('')
	const [subPageTitle] = useState(useCase.name)

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: [subPageTitle, pageTitle] })

	const { goTo } = useNavigate()

	const user = useSelector(state => state.auth.user)


	const toastType = {
		success: "success",
		error: "error"
	}

	const [searchTerm, setSearchTerm] = useState("")
	const [noOfPersona, setNoOfPersona] = useState("0")

	const [selectedUseCaseId, setSelectedUseCaseId] = useState(useCaseId)
	const [orgId, setOrgId] = useState("")

	const { visible: visiblePersonaInfo, show: showPersonaInfo, hide: hidePersonaInfo } = useModal()


	const { currentState, currentPath } = useLocation();
	const participantData = currentState;

	/*================================================================
		Transactions on behalf of external participants - Info Modal
	/==================================================================*/
	const { visible: visibleParticipantInfoModal, show: showParticipantInfoModal, hide: hideParticipantInfoModal } = useModal()

	/*===============================================================
		Transactions on behalf of external participants - T&C Modal
	/================================================================*/
	const { visible: visibleParticipantsTncModal, show: showParticipantsTncModal, hide: hideParticipantsTncModal } = useModal()

	const [enableExternalParticipant, setEnableExternalParticipant] = useState(false)

	function handledEnableExternalParticipant(checked) {
		if (checked) {
			showParticipantsTncModal()
		} else {
			setEnableExternalParticipant(false)
		}
	}

	async function acceptParticipantTnc() {
		setEnableExternalParticipant(true)
		hideParticipantsTncModal();
	}

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { find: findPersonas, findAndSetCurrentPersona, currentItem: persona, items: personaItems, setItems: setPersonaItems, error: errorPersona } = usePersona()

	const getUseCase = {
		useCaseId: selectedUseCaseId,
		organizationId: orgId
	}

	useEffect(() => {
		async function getData() {
			const personasTemp = await findPersonas(getUseCase)
			setPersonaItems(personasTemp)
		}
		getData()
	}, [selectedUseCaseId])

	useEffect(() => {
		if (role === EnrolmentRoles.PARTICIPANT) {
			setPageTitle('Enroll in Use Case')
			setBreadcrumbsItems(['Use Cases', "Enroll"])
			setSteps(EnrolmentSteps[EnrolmentRoles.PARTICIPANT].getEnrolmentSteps(useCase))
			setOrgId(user.organizationId)
		}
		if (role === EnrolmentRoles.ADMIN) {
			setPageTitle('Assign New Use Case')
			setBreadcrumbsItems(['Dashboard', 'Organization Name', pageTitle])
			setSteps(EnrolmentSteps[EnrolmentRoles.ADMIN].getEnrolmentSteps(useCase))
			setOrgId(organizationId)
		}
	}, [useCase])

	//Use Cases API
	const { items: useCases, fetch: findUseCases, setItems: setUseCases, currentItem: useCaseItemObj, setCurrentItem: setUseCaseItem, error: errorUseCase } = useUseCases()

	useEffect(() => {
		async function getData() {

			if (role === EnrolmentRoles.ADMIN) {
				const useCaseList = await findUseCases({ organizationId: organizationId })
				const updatedItems = orderBy(useCaseList, ["name"], ['asc'])
				const newUseCases = updatedItems.filter((useCaseObj) => { return useCaseObj.status !== "enrolled" })
				setUseCases(newUseCases)
			}
			if (role === EnrolmentRoles.PARTICIPANT) {
				const useCaseList = await findUseCases()
				const updatedItems = orderBy(useCaseList, ["name"], ['asc'])
				setUseCases(updatedItems)
			}

		}
		getData()
	}, [])

	//Selected Use Case API
	useEffect(() => {
		async function getData() {
			const selectedUseCaseItem = await findUseCases(getUseCase)
			setUseCaseItem(selectedUseCaseItem[0])
		}

		if (role !== EnrolmentRoles.ADMIN) {
			getData()
		}

	}, [selectedUseCaseId])

	useEffect(() => {
		addToastMessage(errorPersona, toastType.error)
	}, [errorPersona])

	useEffect(() => {
		addToastMessage(errorUseCase, toastType.error)
	}, [errorUseCase])

	/*=================================================
		Use Cases List
	/================================================*/

	const [select, setSelect] = useState({
		useCaseId: "",
		selected: false
	})

	function handleSelect(item) {
		if (select.useCaseId === item.id && select.selected === true) {
			setSelect({
				useCaseId: "",
				selected: false
			})
			setSelectedUseCaseId("")
		} else {
			setSelect({
				useCaseId: item.id,
				selected: true
			})
			setUseCaseItem(item)
			setSelectedUseCaseId(item.id)
		}

	}

	const UseCasesList = () => {
		if (!Array.isArray(useCases)) {
			return <div className='request-enrolment-selectors__col'>No new use case to enroll</div>
		}

		const list = useCases.filter((useCaseObj) => {
			if (searchTerm === "") {
				return useCases
			}
			return useCaseObj?.name.toLowerCase().includes(searchTerm.toLowerCase())
		}).map((item, index) => {
			return (
				<div className="grid__col" key={index}>
					<CardUseCase
						//clickStatus={() => handler(item)}
						id={item.id}
						title={item.name}
						image={item.image}
						description={item.description}
						status={item.status}
						noOfDataElements={item.dataElements.length}
						noOfParticipants={item.participants}
						role={role}
						select={select}
						handleSelect={() => handleSelect(item)}
					/>
				</div>
			)
		})



		return (
			list.length > 0 ? list : <div className='request-enrolment-selectors__col'>No new use case to enroll</div>
		)
	}

	/*=================================================
		Persona List
	/================================================*/

	const handleUpdatePersonaSelect = (item) => {
		const index = personaItems?.findIndex(personaObj => personaObj.id === item.id);
		const updatedItems = produce(personaItems, draft => {
			draft[index].$selected = !draft[index].$selected
		})

		setPersonaItems(updatedItems);
	}

	async function handleCheckInfo(item) {
		await findAndSetCurrentPersona({ personaId: item.id, useCaseId: useCaseId })
		showPersonaInfo()
	}

	function clearPersonaSelection() {
		const updatedItems = produce(personaItems, draft => {
			draft.map((personaObj) => {
				personaObj.$selected = false;
				return personaObj;
			})
		})

		setPersonaItems(updatedItems);
	}

	useEffect(() => {
		const selectedPersona = personaItems?.filter((personaObj) => personaObj.$selected === true).length
		setNoOfPersona(selectedPersona)
	}, [personaItems])

	// Called as a function, not rendered as <PersonaList />: it is redefined on every
	// render, so as a component it would remount the tiles' checkboxes on each click.
	const PersonaList = () => {

		if (!personaItems) {
			return <div className='request-enrolment-selectors__col'>No persona to enroll</div>
		}

		const list = personaItems?.filter((personaObj) => {
			if (searchTerm === "") {
				return personaItems
			}
			return personaObj?.name.toLowerCase().includes(searchTerm.toLowerCase())
		}).map((personaObj, index) => {
			return (
				<div className="request-enrolment-selectors__col" key={index}>
					<SelectorBox label={personaObj?.name} description={personaObj?.description}
						submit={
							() => handleUpdatePersonaSelect(personaObj)
						}
						active={personaObj?.$selected}
						handleCheckInfo={() => handleCheckInfo(personaObj)}
						data-testid={`persona-selector-${index}`}
					/>
				</div>
			)
		})

		return (
			<div className='request-enrolment-selectors__cols'>
				{list.length > 0 ? list : <div className='request-enrolment-selectors__col'>No persona to enroll</div>}
			</div>
		)
	}

	/*=================================================
		To Be Enabled List
	/================================================*/

	const EnabledList = () => {

		let agentPrincipalText = 'No enrolment'
		if (enableExternalParticipant) {
			agentPrincipalText = 'Agent - Principal Function'
		}

		return (
			<div className="layout-request__tile__content__sub__header request-enrolment-admin__confirmation-tile__right__sub-header" >
				<div className="request-enrolment-admin__confirmation-tile__sub-header__icon confirmation-tile__persona-icon">
					<UserGroup />
				</div>
				<div className="request-enrolment-admin__confirmation-tile__sub-header__content">
					{agentPrincipalText}
				</div>
			</div>
		)
	}

	/*=================================================
		Confirmation List
	/================================================*/

	const ConfirmationList = () => {
		return (
			<div className="enrolment-request_confirmation__container">
				<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__left">
					<DataElementCard useCaseItem={useCaseItemObj} />
				</div>

				<div className=" enrolment-request_confirmation_right">
					<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__right">
						<div className="layout-request__tile__content">
							<div className="request-enrolment-confirmation-header request-enrolment-admin__confirmation-tile__right__header">
								<div className="layout-request__title__content__header__left">
									<div className="request-title request-enrolment-confirmation-header-title">
										Selected Personas
									</div>
								</div>
								<div className="layout-request__title__content__header__right">
									<Button text="Edit" ghost submit={() => {
										if (role === EnrolmentRoles.ADMIN) {
											goToStep(getIndexOfStep(EnrolmentSteps[role].choosePersonas))
										}
										if (role === EnrolmentRoles.PARTICIPANT) {
											goToStep(getIndexOfStep(EnrolmentSteps[role].choosePersonas))
										}
									}} />
								</div>
							</div>
							<div className="enrolment-request__tile__content">
								{personaItems?.filter(personaObj => personaObj.$selected === true).map((personaObj, index) => {
									return (
										<div className="layout-request__tile__content__sub__header request-enrolment-admin__confirmation-tile__right__sub-header" key={index}>
											<div className="request-enrolment-admin__confirmation-tile__sub-header__icon confirmation-tile__persona-icon">
												<IconPerson />
											</div>
											<div className="request-enrolment-admin__confirmation-tile__sub-header__content">
												{personaObj.name}
											</div>
										</div>

									)
								})}
							</div>
						</div>
					</div>
					<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__right">
						<div className="layout-request__tile__content">
							<div className="request-enrolment-confirmation-header request-enrolment-admin__confirmation-tile__right__header">
								<div className="layout-request__title__content__header__left">
									<div className="request-title request-enrolment-confirmation-header-title">
										You are enrolling
									</div>
								</div>
								<div className="layout-request__title__content__header__right">
									<Button text="Edit" ghost submit={() => {
										goToStep(getIndexOfStep(EnrolmentSteps[role].choosePersonas))
									}} />
								</div>
							</div>
							<div className="enrolment-request__tile__content">
								<EnabledList />
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	/*=================================================
		Steps Section
	/================================================*/

	const [step, setStep] = useState(1)
	const [steps, setSteps] = useState([])
	const currentActiveStep = useMemo(() => steps[step - 1], [step, steps]);

	function getIndexOfStep(stepName) {
		return steps?.findIndex(step => step === stepName) + 1
	}

	function goToStep(newStep) {
		if (newStep < 1 || newStep > steps?.length) {
			return
		}
		setStep(newStep)
	}

	function prevStep() {
		goToStep(step - 1)
		setSearchTerm("")
	}

	function nextStep() {
		goToStep(step + 1)
	}

	async function confirm() {

		const enrolledPerson = personaItems?.filter(personaObj => personaObj.$selected === true).map(personaObj => {
			const result = {
				personaId: personaObj.id,
				orgId: orgId,
				useCaseId: selectedUseCaseId,
				isAgent: enableExternalParticipant,
			}

			return result
		})

		const [error] = await api.create(enrolledPerson)

		if (error) {
			return addToastMessage(error?.message, toastType.error)
		}

		const locationObj = currentPath
		goTo(`${locationObj}/success`, { from: "enrollment", data: participantData })
	}

	/*=================================================
		Navigate
	/================================================*/

	function onBack() {
		if (role === EnrolmentRoles.PARTICIPANT) {
			goTo(`/use-cases`)
		}

		if (role === EnrolmentRoles.ADMIN) {
			goTo(`/admin/participants/dashboard`, { data: participantData })
		}

	}

	/*=================================================
		Calculated Values
	/================================================*/

	const isAgentEnabled = useCase?.isAgentEnabled ?? useCaseItemObj?.isAgentEnabled

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>
				<Box display="flex" justify="end" />
			</div>

			<Stepper currentStep={step-1} steps={steps}/>

			{role === EnrolmentRoles.ADMIN &&
				<>
					{currentActiveStep === EnrolmentSteps[EnrolmentRoles.ADMIN].chooseUseCase &&
						<>
							<div data-testid="admin-enroll-layout-1" className="layout-enroll">
								<div className="layout-enroll__content">
									<div className="view-info--details">
										<div className="view-info--selector-box-wrap">
											<div className="layout-default__desc__data__element">Choose a Use Case</div>
											<Box display="flex" align="center" mb={4}>
												<InputSuffixIcon maxWidth="556" placeholder="Search" icon={<IconSearch />}
													onChange={(e) => { setSearchTerm(e.target.value) }}
												/>
											</Box>
											<div className="grid">
												<Box display="flex" wrap="wrap" mt={-4} ml={-4}>
													<UseCasesList />
												</Box>
											</div>

										</div>
									</div>
								</div>
							</div>
						</>
					}

					{currentActiveStep === EnrolmentSteps[EnrolmentRoles.ADMIN].choosePersonas &&
						<>
							<div data-testid="admin-enroll-layout-2" className="layout-enroll">
								<div className="layout-enroll__content">
									<div className="view-info--details">
										<div className="view-info--selector-box-wrap">
											<div className="request-enrolment-toggle__cols">
												<div className="layout-default__desc__data__element">Choose personas that best describe your company's profile </div>
											</div>
											<div className="request-enrolment__search-box">
												<InputSuffixIcon data-testid="inputicon" maxWidth="310" placeholder="Search" icon={<IconSearch />}
													onChange={(e) => { setSearchTerm(e.target.value) }}
												/>
												{
													personaItems?.some(personaOb => personaOb.$selected === true) && (
														<>
															<div className="request-enrolment__search-box__selection">{noOfPersona} Selected</div>
															<Button text="Reset Selection" noborder addClass={"request-enrolment__search-box__clear-selection"} submit={() => clearPersonaSelection()} />
														</>
													)
												}

											</div>
											{PersonaList()}
										</div>
									</div>
								</div>
							</div>
						</>
					}

					{currentActiveStep === EnrolmentSteps[EnrolmentRoles.ADMIN].reviewAndConfirm &&
						<>
							<div data-testid="admin-enroll-layout-3" className="layout-enroll">
								<div className="layout-enroll__content">
									<div className="view-info--details">
										<div className="view-info--selector-box-wrap">
											<ConfirmationList />
										</div>
									</div>
								</div>
							</div>
						</>
					}
					<div className='DS__layout__footer'>
						<div data-testid="admin-default-layout" className="layout-default__sticky-actions">
							<div className="layout-default__sticky-actions__left">
								<Button text="Cancel" ghost noborder submit={() => onBack()} />
							</div>
							<div className="layout-default__sticky-actions__right layout-default--flex">
								<div className="layout-default--margin-right">
									{step > 0 &&
										<Button text="Previous" ghost submit={() => prevStep()} />
									}
								</div>
								{currentActiveStep === EnrolmentSteps[EnrolmentRoles.ADMIN].chooseUseCase &&
									<Button text="Next" disabled={!select.selected} submit={() => nextStep()} />

								}
								{currentActiveStep === EnrolmentSteps[EnrolmentRoles.ADMIN].choosePersonas &&
									<Button text="Next" disabled={!personaItems?.some(personaObj => personaObj.$selected === true)} submit={() => nextStep()} />

								}
								{currentActiveStep === EnrolmentSteps[EnrolmentRoles.ADMIN].reviewAndConfirm &&
									<Button text="Save" submit={() => confirm()} full />

								}
							</div>
						</div>
					</div>
				</>
			}

			{role === EnrolmentRoles.PARTICIPANT &&
				<>
					{currentActiveStep === EnrolmentSteps[EnrolmentRoles.PARTICIPANT].choosePersonas &&
						<>
							<div data-testid="participant-enroll-layout-1" className="layout-enroll">
								<div className="layout-enroll__content">
									<div className="view-info--details">
										<div className="view-info--selector-box-wrap">
											<Box display="flex" wrap="wrap" justify="between">
												<div className="layout-default__desc__data__element">Choose personas that best describe your company's profile </div>

												{useCase.isAgentEnabled && (
													<div className="layout-enrolment__toggle__content">
														<div className="layout-enrolment__info" data-testid="agent-principal-info" onClick={() => { showParticipantInfoModal() }}>
															<IconEye />
														</div>
														<div>Enable transactions on behalf of external participants:</div>
														<ToggleSwitch3
															id="enableExternalParticipant"
															checked={enableExternalParticipant}
															onChange={handledEnableExternalParticipant}
															data-testid="enable-external-participant-toggle"
														/>
														<div>{enableExternalParticipant ? "Yes" : "No"}</div>
													</div>
												)}
											</Box>
											<Box display="flex" align="center" mb={4}>
												<InputSuffixIcon maxWidth="310" placeholder="Search" icon={<IconSearch />}
													//defaultValue = {search || ""}
													onChange={(e) => { setSearchTerm(e.target.value) }}
												/>
												{
													personaItems?.some(personaObj => personaObj.$selected === true) && (
														<>
															<div className="request-enrolment__search-box__selection">{noOfPersona} Selected</div>
															<Button text="Reset Selection" noborder addClass={"request-enrolment__search-box__clear-selection"} submit={() => clearPersonaSelection()} />
														</>
													)
												}
											</Box>
											{PersonaList()}
										</div>
									</div>
								</div>
							</div>
						</>
					}

					{currentActiveStep === EnrolmentSteps[EnrolmentRoles.PARTICIPANT].reviewAndConfirm &&
						<>
							<div data-testid="participant-enroll-layout-2" className="layout-enroll">
								<div className="layout-enroll__content">
									<div className="view-info--details">
										<div className="view-info--selector-box-wrap">
											<ConfirmationList />
										</div>
									</div>
								</div>
							</div>
						</>
					}

					<div data-testid="participant-default-layout" className="layout-default__sticky-actions">
						<div className="layout-default__sticky-actions__left">
							<Button text="Cancel" ghost noborder submit={() => onBack()} />
						</div>
						<div className="layout-default__sticky-actions__right layout-default--flex">
							<div className="layout-default--margin-right">
								{step > 0 &&
									<Button text="Previous" ghost submit={() => prevStep()} />
								}
							</div>
							{currentActiveStep === EnrolmentSteps[EnrolmentRoles.PARTICIPANT].choosePersonas &&
								<Button text="Next" disabled={!personaItems?.some(personaObj => personaObj.$selected === true)} submit={() => nextStep()} />

							}
							{currentActiveStep === EnrolmentSteps[EnrolmentRoles.PARTICIPANT].reviewAndConfirm &&
								<Button text="Save" id="enroll-use-case-submit-button" submit={() => confirm()} full />
							}
						</div>
					</div>
				</>
			}

			<PersonaInfoModal
				visible={visiblePersonaInfo}
				hide={hidePersonaInfo}
				persona={persona} />

			<AgentPrincipalInformationModal
				visible={visibleParticipantInfoModal}
				hide={hideParticipantInfoModal}
				show={showParticipantInfoModal} />

			<AgentPrincipalTnCModal
				visible={visibleParticipantsTncModal}
				hide={hideParticipantsTncModal}
				show={showParticipantsTncModal}
				acceptParticipantTnc={acceptParticipantTnc} />

			<Toast toastList={toastList} />
		</>
	)
}
export default Page
